import { Box, Menu, MenuItem } from '@mui/material';
import type { ContractResponse } from '@timelog/pm-shared';
import { ownHours } from '@timelog/pm-shared';
import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteContractDialog } from './DeleteContractDialog';
import { useFinancials } from './editor';

import { ApiError } from '@/api';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { CONTRACT_TYPE_STYLES } from '@/components/planning/ContractChip';
import { MICRO_LABEL_TRACKING, MicroLabel } from '@/components/ui';
import { formatAmountAbbrev, formatHours, isBoolean, usePersistedState } from '@/lib';
import { layout } from '@/theme';

/**
 * The collapsible contracts rail (design: 48px badges ⇄ 200px list): one row
 * per contract with the type letter badge, revenue and own-work hours, a
 * kebab (Duplicate / Delete), Add contract, and the Σ footer.
 *
 * It starts collapsed. Opening itself put a list offering a *second* contract in
 * front of someone who had just made their first, when the focus belongs on the
 * contract they came to work on. The exception is the SECOND contract
 * (2026-10-08, the pilot's ask): creating it opens the rail, because that is
 * the moment there is a list worth finding. Collapsed is a full state, not a
 * degraded one: the badges select a contract exactly as the rows do, Add
 * contract survives, and `?contract=` reaches any of them without the rail at
 * all.
 *
 * Left as a fixed-width rail rather than a resizable panel: the design system
 * has exactly one panel pattern — a main column with a right-hand drawer — and a
 * draggable left rail would be a new one. See the note in
 * docs/adr/0013-resizable-panels-for-page-layout.md.
 */

/** Ties the collapse toggle's aria-controls to the list it hides. */
const CONTRACTS_LIST_ID = 'contracts-rail-list';

const LetterBadge = ({
  contract,
  active,
  size = 22,
}: {
  contract: ContractResponse;
  active: boolean;
  size?: number;
}) => {
  const style = CONTRACT_TYPE_STYLES[contract.type];
  const { t } = useTranslation('pmCommon');
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--border-radius-pill)',
        border: `var(--border-width-thick) solid ${style.line}`,
        bgcolor: active ? style.badgeBg : 'transparent',
        color: style.badgeFg,
        fontSize: 'var(--font-size-label)',
        fontWeight: 'var(--font-weight-bold)',
        fontFamily: 'var(--font-family-mono)',
      }}
    >
      {t(`billingLetter.${contract.type}`)}
    </Box>
  );
};

export const ContractsPanel = () => {
  const { effectiveContracts, blendedCostRate, actions } = useFinancials();
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);
  /*
    Collapsed by default, and persisted — which together mean exactly "shut
    until you say otherwise": `usePersistedState` only writes on a toggle, so a
    stored value exists only where the user deliberately clicked, and that
    choice then survives navigating between Planning and Financials (it used to
    reset to open every time, which is why this is persisted at all).
  */
  const [open, setOpen] = usePersistedState('pm.contractsRail.open', false, isBoolean);
  /*
    One → two contracts, seen while the page is open (Add contract from either
    button, or Duplicate), opens the rail: a second contract is when the
    contracts menu starts to matter, and the pilot found people never
    discovered it shut. Only that step, and only as it happens — loading a
    project that already has several leaves a deliberately closed rail closed,
    and a third or fourth contract does not reopen it.
  */
  const contractCount = effectiveContracts.length;
  const previousCount = useRef(contractCount);
  useEffect(() => {
    if (previousCount.current === 1 && contractCount === 2) setOpen(true);
    previousCount.current = contractCount;
  }, [contractCount, setOpen]);
  const [kebab, setKebab] = useState<{ anchor: HTMLElement; contractId: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    contract: ContractResponse;
    message: string;
  } | null>(null);

  const activeId = useFinancials().state.contractId;
  const totalRevenue = effectiveContracts.reduce(
    (sum, contract) => sum + contract.document.revenue,
    0,
  );
  const totalHours = effectiveContracts.reduce(
    (sum, contract) => sum + ownHours(contract.document, blendedCostRate),
    0,
  );

  const openKebab = (event: MouseEvent<HTMLElement>, contractId: string) => {
    event.stopPropagation();
    setKebab({ anchor: event.currentTarget, contractId });
  };

  const requestDelete = async (contract: ContractResponse, reassignTo?: string) => {
    try {
      await actions.deleteContract(contract.id, reassignTo);
      setPendingDelete(null);
    } catch (error) {
      if (error instanceof ApiError && error.isConflict) {
        setPendingDelete({ contract, message: error.message });
        return;
      }
      throw error;
    }
  };

  const kebabContract = effectiveContracts.find((contract) => contract.id === kebab?.contractId);

  return (
    <Box
      data-automation-id="contracts-panel"
      data-state={open ? 'open' : 'collapsed'}
      sx={{
        flex: 'none',
        width: open ? layout.contractsRailOpen : layout.contractsRailCollapsed,
        bgcolor: 'var(--bg-panel)',
        borderRadius: 'var(--border-radius-x-large)',
        border: 'var(--border-width-hairline) solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // Duration token, so the reduced-motion block in tokens.css zeroes it.
        transition: 'width var(--duration-base) var(--easing-standard)',
        alignSelf: 'stretch',
      }}
    >
      {/* header / collapse toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          px: open ? 'var(--spacing-unit-2)' : 0,
          pt: 'var(--spacing-unit-1)',
          pb: 'var(--spacing-unit-1)',
          borderBottom: open ? 'var(--border-width-thin) solid var(--border-divider)' : 'none',
        }}
      >
        {open ? <MicroLabel>{t('pmCommon:term.Contracts')}</MicroLabel> : null}
        <Box
          component="button"
          type="button"
          data-automation-id="contracts-panel-toggle"
          onClick={() => setOpen(!open)}
          /*
           * The only content is a MaterialSymbol, which sets aria-hidden — so
           * without an explicit label this control had no accessible name at all.
           */
          aria-label={open ? t('contractsRail.CollapseList') : t('contractsRail.ExpandList')}
          aria-expanded={open}
          aria-controls={CONTRACTS_LIST_ID}
          title={open ? t('contractsRail.CollapseList') : t('contractsRail.ExpandList')}
          sx={{
            width: 'var(--input-height-tiny)',
            height: 'var(--input-height-tiny)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            bgcolor: 'transparent',
            borderRadius: 'var(--border-radius-pill)',
            color: 'var(--fg-3)',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--fg-1)' },
            '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '1px' },
          }}
        >
          <MaterialSymbol name={open ? 'chevron_left' : 'chevron_right'} size={16} />
        </Box>
      </Box>

      {/* rows */}
      <Box
        id={CONTRACTS_LIST_ID}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        {effectiveContracts.map((contract) =>
          open ? (
            <Box
              key={contract.id}
              data-automation-id="contract-row"
              data-contract-id={contract.id}
              data-active={contract.id === activeId ? 'true' : 'false'}
              onClick={() => void actions.selectContract(contract.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                cursor: 'pointer',
                bgcolor: contract.id === activeId ? 'var(--color-fog-10)' : 'transparent',
                borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
                '&:hover': { bgcolor: 'var(--bg-hover)' },
              }}
            >
              <LetterBadge contract={contract} active={contract.id === activeId} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: contract.id === activeId ? 500 : 400,
                    color: contract.id === activeId ? 'var(--fg-1)' : 'var(--fg-2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  data-automation-id="contract-row-name"
                >
                  {contract.name}
                </Box>
                <Box
                  sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, color: 'var(--fg-3)' }}
                  data-automation-id="contract-row-amount"
                >
                  {contract.document.revenue > 0
                    ? `${formatAmountAbbrev(contract.document.revenue)} DKK`
                    : '—'}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 11,
                    color: 'var(--color-pink-80)',
                  }}
                >
                  {contract.document.revenue > 0
                    ? t('units.Hours', {
                        value: formatHours(ownHours(contract.document, blendedCostRate)),
                      })
                    : '—'}
                </Box>
              </Box>
              <Box
                component="button"
                type="button"
                data-automation-id="contract-row-kebab"
                onClick={(event: MouseEvent<HTMLElement>) => openKebab(event, contract.id)}
                sx={{
                  width: 24,
                  height: 24,
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  bgcolor: 'transparent',
                  borderRadius: 'var(--border-radius-small)',
                  color: 'var(--fg-3)',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--fg-2)' },
                }}
              >
                <MaterialSymbol name="more_vert" size={16} />
              </Box>
            </Box>
          ) : (
            <Box
              key={contract.id}
              component="button"
              type="button"
              data-automation-id="contract-badge"
              data-contract-id={contract.id}
              onClick={() => void actions.selectContract(contract.id)}
              sx={{
                border: 'none',
                bgcolor: 'transparent',
                display: 'flex',
                justifyContent: 'center',
                py: 0.5,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              }}
              title={contract.name}
            >
              <LetterBadge contract={contract} active={contract.id === activeId} size={28} />
            </Box>
          ),
        )}

        <Box
          component="button"
          type="button"
          data-automation-id="add-contract"
          onClick={() => void actions.createContract()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'flex-start' : 'center',
            gap: 1,
            width: '100%',
            px: open ? '14px' : 0,
            py: 1,
            border: 'none',
            bgcolor: 'transparent',
            fontSize: 14,
            fontFamily: 'var(--font-family)',
            color: 'var(--fg-2)',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'var(--bg-hover)' },
          }}
        >
          <MaterialSymbol name="add" size={16} />
          {open ? t('actions.AddContract') : null}
        </Box>
      </Box>

      {/* Σ footer */}
      <Box
        sx={{
          px: open ? '14px' : '4px',
          py: 1,
          borderTop: 'var(--border-width-thin) solid var(--border-divider)',
          textAlign: open ? 'left' : 'center',
        }}
        data-automation-id="contracts-panel-sum"
      >
        {open ? (
          <Box
            sx={{
              fontSize: 11,
              color: 'var(--fg-3)',
              mb: 0.5,
              textTransform: 'uppercase',
              letterSpacing: MICRO_LABEL_TRACKING,
            }}
          >
            {t('contractsRail.Sum')}
          </Box>
        ) : null}
        <Box
          sx={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: open ? 14 : 10,
            fontWeight: 500,
            color: 'var(--fg-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          data-automation-id="contracts-total"
        >
          {totalRevenue > 0 ? `${formatAmountAbbrev(totalRevenue)}${open ? ' DKK' : ''}` : '—'}
        </Box>
        {open ? (
          <Box
            sx={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 11,
              color: 'var(--color-pink-80)',
              mt: 0.5,
            }}
            data-automation-id="contracts-total-hours"
          >
            {totalHours > 0 ? t('units.Hours', { value: formatHours(totalHours) }) : '—'}
          </Box>
        ) : null}
      </Box>

      {/* kebab menu */}
      <Menu
        anchorEl={kebab?.anchor ?? null}
        open={kebab !== null}
        onClose={() => setKebab(null)}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem
          data-automation-id="contract-duplicate"
          onClick={() => {
            const id = kebab?.contractId;
            setKebab(null);
            if (id) void actions.duplicateContract(id);
          }}
        >
          {t('contractsRail.Duplicate')}
        </MenuItem>
        {effectiveContracts.length > 1 ? (
          <MenuItem
            data-automation-id="contract-delete"
            sx={{ color: 'var(--color-error-80)' }}
            onClick={() => {
              const contract = kebabContract;
              setKebab(null);
              if (contract) void requestDelete(contract);
            }}
          >
            {t('contractsRail.Delete')}
          </MenuItem>
        ) : null}
      </Menu>

      <DeleteContractDialog
        contract={pendingDelete?.contract ?? null}
        message={pendingDelete?.message ?? ''}
        siblings={effectiveContracts.filter(
          (contract) => contract.id !== pendingDelete?.contract.id,
        )}
        onConfirm={(reassignTo) =>
          pendingDelete ? requestDelete(pendingDelete.contract, reassignTo) : Promise.resolve()
        }
        onClose={() => setPendingDelete(null)}
      />
    </Box>
  );
};
